import { useState, useEffect } from "react";
import { useAuth, apiClient } from "../App";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { 
  Palette, Library, Clock, Download, Eye, Trash2, 
  Loader2, FolderOpen, AlertCircle, Crown, ChevronLeft, 
  ChevronRight, Grid, List, Search, Filter, Share2
} from "lucide-react";
import { Input } from "../components/ui/input";
import ShareButtons from "../components/ShareButtons";

const MyLibrary = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasPermanentStorage, setHasPermanentStorage] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    fetchLibrary();
  }, [page]);

  const fetchLibrary = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/library", {
        params: { page, limit: 12 }
      });
      setProjects(response.data.projects);
      setTotalPages(response.data.total_pages);
      setHasPermanentStorage(response.data.has_permanent_storage);
    } catch (error) {
      console.error("Error fetching library:", error);
      toast.error("Failed to load library");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (projectId) => {
    if (!confirm("Are you sure you want to delete this project?")) return;
    
    setDeleting(projectId);
    try {
      await apiClient.delete(`/library/${projectId}`);
      toast.success("Project deleted");
      fetchLibrary();
    } catch (error) {
      toast.error("Failed to delete project");
    } finally {
      setDeleting(null);
    }
  };

  const filteredProjects = searchQuery 
    ? projects.filter(p => p.theme.toLowerCase().includes(searchQuery.toLowerCase()))
    : projects;

  const getExpiryText = (project) => {
    if (project.days_until_expiry < 0) return "Permanent";
    if (project.days_until_expiry === 0) return "Expires today";
    if (project.days_until_expiry === 1) return "Expires tomorrow";
    return `${project.days_until_expiry} days left`;
  };

  const getExpiryColor = (project) => {
    if (project.days_until_expiry < 0) return "text-green-600 bg-green-50";
    if (project.days_until_expiry <= 1) return "text-red-600 bg-red-50";
    if (project.days_until_expiry <= 3) return "text-orange-600 bg-orange-50";
    return "text-slate-600 bg-slate-50";
  };

  return (
    <div className="min-h-screen bg-hero-gradient">
      {/* Header */}
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
              <ChevronLeft className="w-5 h-5" />
              <span className="font-medium">Back to Dashboard</span>
            </Link>

            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-xl text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                ColorWonder
              </span>
            </Link>

            <Button
              onClick={() => navigate("/generate")}
              className="h-10 px-6 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold"
            >
              Create New
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-8">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              <Library className="w-8 h-8 text-violet-600" />
              My Library
            </h1>
            <p className="text-slate-600 mt-1">
              {hasPermanentStorage 
                ? "All your creations, stored forever" 
                : "Your recent creations (stored for 7 days)"}
            </p>
          </div>

          {/* Search and View Toggle */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by theme..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-10 w-64 rounded-full"
              />
            </div>
            <div className="flex items-center bg-slate-100 rounded-full p-1">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-full transition-colors ${viewMode === "grid" ? "bg-white shadow" : ""}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded-full transition-colors ${viewMode === "list" ? "bg-white shadow" : ""}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Storage Upgrade Banner */}
        {!hasPermanentStorage && (
          <div className="bg-gradient-to-r from-violet-50 to-indigo-50 rounded-2xl p-4 mb-8 flex items-center justify-between border border-violet-100">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-violet-600" />
              <div>
                <p className="font-medium text-slate-800">Files expire after 7 days</p>
                <p className="text-sm text-slate-600">Upgrade to Plus or Pro for permanent storage</p>
              </div>
            </div>
            <Button
              onClick={() => navigate("/pricing")}
              className="h-10 px-6 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold"
            >
              <Crown className="w-4 h-4 mr-2" />
              Upgrade
            </Button>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
          </div>
        ) : filteredProjects.length === 0 ? (
          /* Empty State */
          <div className="text-center py-20">
            <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-6">
              <FolderOpen className="w-10 h-10 text-slate-400" />
            </div>
            <h2 className="text-xl font-semibold text-slate-800 mb-2">
              {searchQuery ? "No projects found" : "Your library is empty"}
            </h2>
            <p className="text-slate-600 mb-6">
              {searchQuery 
                ? "Try a different search term" 
                : "Create your first coloring book to see it here"}
            </p>
            {!searchQuery && (
              <Button
                onClick={() => navigate("/generate")}
                className="h-12 px-8 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold"
              >
                Create Your First Book
              </Button>
            )}
          </div>
        ) : (
          /* Projects Grid/List */
          <div className={viewMode === "grid" 
            ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" 
            : "space-y-4"
          }>
            {filteredProjects.map((project) => (
              viewMode === "grid" ? (
                /* Grid View Card */
                <div
                  key={project.project_id}
                  className="bg-white rounded-2xl border border-slate-100 overflow-hidden hover:shadow-xl transition-shadow group"
                >
                  {/* Thumbnail Placeholder */}
                  <div className="aspect-[4/3] bg-gradient-to-br from-violet-100 to-pink-100 relative flex items-center justify-center">
                    <Palette className="w-12 h-12 text-violet-300" />
                    
                    {/* Batch Badge */}
                    {project.batch_id && (
                      <div className="absolute top-3 left-3 bg-violet-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                        Batch #{project.variation_number}
                      </div>
                    )}
                    
                    {/* Status Badge */}
                    <div className={`absolute top-3 right-3 text-xs font-medium px-2 py-1 rounded-full ${
                      project.status === "completed" ? "bg-green-100 text-green-700" :
                      project.status === "generating" ? "bg-blue-100 text-blue-700" :
                      "bg-slate-100 text-slate-700"
                    }`}>
                      {project.status}
                    </div>

                    {/* Hover Actions */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                      <Button
                        onClick={() => navigate(`/preview/${project.project_id}`)}
                        className="h-10 w-10 rounded-full bg-white text-slate-800"
                        data-testid={`view-project-${project.project_id}`}
                      >
                        <Eye className="w-5 h-5" />
                      </Button>
                      <ShareButtons 
                        projectId={project.project_id} 
                        theme={project.theme} 
                        variant="icon"
                      />
                      <Button
                        onClick={() => handleDelete(project.project_id)}
                        disabled={deleting === project.project_id}
                        variant="destructive"
                        className="h-10 w-10 rounded-full"
                        data-testid={`delete-project-${project.project_id}`}
                      >
                        {deleting === project.project_id ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Trash2 className="w-5 h-5" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="p-4">
                    <h3 className="font-semibold text-slate-800 truncate mb-1">
                      {project.theme}
                    </h3>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">
                        {project.page_count} pages
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getExpiryColor(project)}`}>
                        <Clock className="w-3 h-3 inline mr-1" />
                        {getExpiryText(project)}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                /* List View Row */
                <div
                  key={project.project_id}
                  className="bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-4 hover:shadow-lg transition-shadow"
                >
                  {/* Thumbnail */}
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-violet-100 to-pink-100 flex items-center justify-center flex-shrink-0">
                    <Palette className="w-6 h-6 text-violet-300" />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-800 truncate">{project.theme}</h3>
                    <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                      <span>{project.page_count} pages</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getExpiryColor(project)}`}>
                        {getExpiryText(project)}
                      </span>
                      {project.batch_id && (
                        <span className="text-violet-600">Batch #{project.variation_number}</span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => navigate(`/preview/${project.project_id}`)}
                      variant="outline"
                      className="h-10 px-4 rounded-full"
                      data-testid={`view-project-list-${project.project_id}`}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View
                    </Button>
                    <ShareButtons 
                      projectId={project.project_id} 
                      theme={project.theme} 
                      variant="icon"
                    />
                    <Button
                      onClick={() => handleDelete(project.project_id)}
                      disabled={deleting === project.project_id}
                      variant="ghost"
                      className="h-10 w-10 rounded-full text-slate-400 hover:text-red-600"
                      data-testid={`delete-project-list-${project.project_id}`}
                    >
                      {deleting === project.project_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              )
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8">
            <Button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              variant="outline"
              className="h-10 w-10 rounded-full"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            
            <span className="px-4 text-slate-600">
              Page {page} of {totalPages}
            </span>
            
            <Button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              variant="outline"
              className="h-10 w-10 rounded-full"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default MyLibrary;
